import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users, IndianRupee, IdCard, Gift, TrendingUp, Search, Plus, Minus } from "lucide-react";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN");

function StatCard({ label, value, icon: Icon, sub, tone = "black" }) {
  const tones = {
    black: "bg-white text-black",
    primary: "bg-[#002FA7] text-white",
    green: "bg-[#22C55E] text-black",
    yellow: "bg-[#EAB308] text-black",
  };
  return (
    <div className={`${tones[tone]} brutal-border brutal-shadow p-5`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-widest opacity-80">{label}</span>
        <Icon className="w-4 h-4" strokeWidth={2}/>
      </div>
      <div className="font-display font-black text-3xl">{value}</div>
      {sub && <div className="font-mono text-[10px] uppercase tracking-widest opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [grants, setGrants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [grantOpen, setGrantOpen] = useState(null); // user object
  const [grantQty, setGrantQty] = useState(10);
  const [grantNote, setGrantNote] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); }
    catch (e) { console.error("Load stats failed:", e); toast.error(e.response?.data?.detail || "Load stats failed"); }
  }, []);
  const loadUsers = useCallback(async (search = "") => {
    try { const { data } = await api.get("/admin/users", { params: { q: search } }); setUsers(data); }
    catch (e) { console.error("Load users failed:", e); toast.error("Load users failed"); }
  }, []);
  const loadGrants = useCallback(async () => {
    try { const { data } = await api.get("/admin/grants"); setGrants(data); }
    catch (e) { console.error("Load grants failed:", e); }
  }, []);
  const loadOrders = useCallback(async () => {
    try { const { data } = await api.get("/admin/orders"); setOrders(data); }
    catch (e) { console.error("Load orders failed:", e); }
  }, []);

  useEffect(() => {
    loadStats(); loadUsers(); loadGrants(); loadOrders();
  }, [loadStats, loadUsers, loadGrants, loadOrders]);

  const submitGrant = async () => {
    if (!grantOpen) return;
    if (!grantQty || isNaN(grantQty)) { toast.error("Enter quantity"); return; }
    if (!grantNote.trim()) { toast.error("Note is required for audit"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${grantOpen.user_id}/grant-credits`, { quantity: parseInt(grantQty), note: grantNote });
      toast.success(`${grantQty > 0 ? "Granted" : "Deducted"} ${Math.abs(grantQty)} credits to ${grantOpen.email}`);
      setGrantOpen(null); setGrantQty(10); setGrantNote("");
      loadUsers(q); loadStats(); loadGrants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!user?.is_admin) {
    return (
      <div className="bg-white brutal-border brutal-shadow p-10 text-center">
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF3B30] mb-2">// 403</div>
        <h1 className="font-display font-black text-3xl">Admin access required</h1>
        <p className="text-gray-600 mt-2">Your account ({user?.email}) is not an admin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#FF3B30] font-bold mb-2">// Admin</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Control Room</h1>
        <p className="text-gray-600 mt-1">Revenue, users, and offline credit grants.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {["overview", "users", "orders", "grants"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold brutal-border ${tab === t ? "bg-black text-white" : "bg-white hover:bg-gray-100"}`}
            data-testid={`tab-${t}`}
          >{t}</button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total Revenue" value={money(stats.total_revenue_inr)} icon={IndianRupee} sub={`${stats.total_payments} payments`} tone="primary"/>
            <StatCard label="Revenue (30d)" value={money(stats.revenue_30d_inr)} icon={TrendingUp} sub={`${stats.payments_30d} payments`} tone="green"/>
            <StatCard label="Users" value={stats.total_users} icon={Users} sub={`+${stats.new_users_7d} last 7d`}/>
            <StatCard label="ID Cards" value={stats.total_cards} icon={IdCard} sub={`+${stats.cards_7d} last 7d`}/>
            <StatCard label="Cards (30d)" value={stats.cards_30d} icon={IdCard}/>
            <StatCard label="Offline Credits" value={stats.total_offline_credits} icon={Gift} sub="Total granted" tone="yellow"/>
          </div>

          <div className="bg-white brutal-border brutal-shadow">
            <div className="p-4 border-b-2 border-black">
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500">// Top Organizations</div>
              <h3 className="font-display font-bold text-xl">By ID Cards Generated</h3>
            </div>
            {stats.top_orgs.length === 0 ? (
              <div className="p-8 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No data yet</div>
            ) : (
              <div className="divide-y-2 divide-black">
                {stats.top_orgs.map((o, i) => (
                  <div key={o.user_id} className="p-4 flex items-center justify-between" data-testid={`top-org-${i}`}>
                    <div className="flex items-center gap-4">
                      <div className="font-display font-black text-2xl text-[#002FA7] w-8">{i+1}</div>
                      <div>
                        <div className="font-bold">{o.name || o.email}</div>
                        <div className="font-mono text-xs text-gray-500">{o.email}</div>
                      </div>
                    </div>
                    <div className="font-mono text-xs uppercase tracking-widest">
                      <span className="font-display font-black text-2xl">{o.cards}</span> cards
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* USERS */}
      {tab === "users" && (
        <div className="space-y-4">
          <div className="flex gap-2 items-center">
            <Search className="w-4 h-4"/>
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && loadUsers(q)}
              placeholder="Search email or name..."
              className="brutal-input flex-1"
              data-testid="user-search"
            />
            <button onClick={() => loadUsers(q)} className="bg-black text-white px-4 py-3 brutal-border brutal-shadow-sm brutal-hover font-mono text-xs uppercase tracking-widest font-bold">Search</button>
          </div>

          <div className="bg-white brutal-border brutal-shadow overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Name / Email</th>
                  <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Provider</th>
                  <th className="p-3 text-right font-mono text-xs uppercase tracking-widest">Credits</th>
                  <th className="p-3 text-right font-mono text-xs uppercase tracking-widest">Cards</th>
                  <th className="p-3 text-right font-mono text-xs uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-t-2 border-black" data-testid={`user-row-${u.user_id}`}>
                    <td className="p-3">
                      <div className="font-bold flex items-center gap-2">{u.name || "—"} {u.is_admin && <span className="bg-[#FF3B30] text-white px-2 py-0.5 font-mono text-[10px] uppercase">Admin</span>}</div>
                      <div className="font-mono text-xs text-gray-500">{u.email}</div>
                    </td>
                    <td className="p-3 font-mono text-xs uppercase">{u.auth_provider}</td>
                    <td className="p-3 text-right font-mono font-bold">{u.credits}</td>
                    <td className="p-3 text-right font-mono">{u.cards_count}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => { setGrantOpen(u); setGrantQty(10); setGrantNote(""); }} className="inline-flex items-center gap-1 bg-[#22C55E] text-black px-3 py-1 brutal-border brutal-shadow-sm font-mono text-xs uppercase font-bold" data-testid={`grant-${u.user_id}`}>
                        <Gift className="w-3 h-3"/> Grant
                      </button>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ORDERS */}
      {tab === "orders" && (
        <div className="bg-white brutal-border brutal-shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white">
              <tr>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Order ID</th>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">User</th>
                <th className="p-3 text-right font-mono text-xs uppercase tracking-widest">Qty</th>
                <th className="p-3 text-right font-mono text-xs uppercase tracking-widest">Amount</th>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Status</th>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.order_id} className="border-t-2 border-black" data-testid={`order-row-${o.order_id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_id}</td>
                  <td className="p-3">
                    <div className="text-sm">{o.user_name || "—"}</div>
                    <div className="font-mono text-xs text-gray-500">{o.user_email}</div>
                  </td>
                  <td className="p-3 text-right font-mono">{o.quantity}</td>
                  <td className="p-3 text-right font-mono font-bold">{money(o.amount_inr)}</td>
                  <td className="p-3">
                    <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${o.status === "paid" ? "bg-[#22C55E] text-black" : "bg-gray-200 text-black"}`}>{o.status}</span>
                  </td>
                  <td className="p-3 font-mono text-xs">{new Date(o.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No orders yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* GRANTS */}
      {tab === "grants" && (
        <div className="bg-white brutal-border brutal-shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white">
              <tr>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">User</th>
                <th className="p-3 text-right font-mono text-xs uppercase tracking-widest">Qty</th>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Note</th>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Granted By</th>
                <th className="p-3 text-left font-mono text-xs uppercase tracking-widest">Date</th>
              </tr>
            </thead>
            <tbody>
              {grants.map(g => (
                <tr key={g.id} className="border-t-2 border-black" data-testid={`grant-row-${g.id}`}>
                  <td className="p-3 font-mono text-xs">{g.user_email}</td>
                  <td className="p-3 text-right font-mono font-bold" style={{color: g.quantity > 0 ? "#22C55E" : "#FF3B30"}}>{g.quantity > 0 ? "+" : ""}{g.quantity}</td>
                  <td className="p-3 text-sm">{g.note || "—"}</td>
                  <td className="p-3 font-mono text-xs">{g.granted_by_email}</td>
                  <td className="p-3 font-mono text-xs">{new Date(g.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {grants.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center font-mono text-xs uppercase tracking-widest text-gray-500">No grants yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* GRANT MODAL */}
      {grantOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setGrantOpen(null)}>
          <div className="bg-white brutal-border brutal-shadow-lg p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="grant-modal">
            <div className="font-mono text-xs uppercase tracking-widest text-[#22C55E] font-bold mb-2">// Offline credit grant</div>
            <h3 className="font-display font-black text-2xl mb-1">Grant credits</h3>
            <p className="text-sm text-gray-600 mb-4">To: <span className="font-bold">{grantOpen.email}</span> · Current: <span className="font-mono font-bold">{grantOpen.credits}</span></p>

            <div className="space-y-3">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Quantity (use negative to deduct)</label>
                <div className="flex gap-2">
                  <button onClick={() => setGrantQty(q => (parseInt(q)||0) - 5)} className="brutal-border px-3 py-2 bg-white hover:bg-gray-100"><Minus className="w-4 h-4"/></button>
                  <input type="number" value={grantQty} onChange={e => setGrantQty(e.target.value)} className="brutal-input flex-1 text-center font-display font-black text-2xl" data-testid="grant-qty"/>
                  <button onClick={() => setGrantQty(q => (parseInt(q)||0) + 5)} className="brutal-border px-3 py-2 bg-white hover:bg-gray-100"><Plus className="w-4 h-4"/></button>
                </div>
                <div className="flex gap-2 mt-2">
                  {[10, 50, 100, 500].map(v => (
                    <button key={v} onClick={() => setGrantQty(v)} className="flex-1 brutal-border px-2 py-1 font-mono text-xs uppercase font-bold bg-white hover:bg-gray-100">+{v}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest font-bold mb-1">Note / Reason</label>
                <input value={grantNote} onChange={e => setGrantNote(e.target.value)} placeholder="e.g. Payment gateway failed, refund reference #123" className="brutal-input w-full" data-testid="grant-note"/>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <button onClick={() => setGrantOpen(null)} className="bg-white brutal-border brutal-shadow-sm brutal-hover px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold" data-testid="grant-cancel">Cancel</button>
              <button onClick={submitGrant} disabled={busy} className="bg-[#22C55E] text-black brutal-border brutal-shadow brutal-hover px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-60" data-testid="grant-confirm">{busy ? "..." : `Apply (${grantQty > 0 ? "+" : ""}${grantQty})`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
